import React, { useEffect, useState } from 'react'

// React components
import { GridData } from './GridData'
import { GatewayModal } from './GatewayModal'

// Kendo components
import { Certificate, EDIGatewayView, Gateway } from './Interfaces'
import { Dialog } from '@progress/kendo-react-dialogs'
import { getAPIURL } from '../../GlobalComponents/URLs'

import '../EDIForms.scss'
import styles from '.././EDIForms.module.scss'
import FastQuote from '../../FastQuote/FastQuote'
import { AWCButton, BUTTON_TYPES } from '../../GlobalComponents/Inputs/Inputs'

export const EDIDashboard: React.FC<{}> = () => {
    document.title = 'EDI Gateways'
    // Add/save new gateway
    const saveEDIGatewayURL = getAPIURL() + 'edi/SaveEDIGateway'
    const getEDIGatewayURL = getAPIURL() + 'edi/GetEDIGateway'
    const deleteEDIGatewayURL = getAPIURL() + 'edi/DeleteEDIGateway'
    const testCertificateURL = getAPIURL() + 'edi/SendAS2Test'

    // States for Gateway data
    const [data, setData] = useState<Array<Gateway>>()
    const [loading, setLoading] = useState<boolean>(true)
    const [error, setError] = useState<string>('')

    // Create/edit gateway modal form
    const [openForm, setOpenForm] = React.useState(false)

    // Empty gateway
    const emptyGateway: Gateway = {
        Name: '',
        Username: '',
        Password: '',
        URL: '',
        Port: 0,
        OutBoundDirectory: '',
        ProviderReceiveDirectory: '',
        ProviderSendDirectory: '',
        DeleteOnGetRequired: false,
        isActive: false,
        isANSIX12: false,
        Protocol: '',
        AS2SenderID: '',
        ContactInfo: '',
        PartnerCertificateExpires: '',
        CertificateName: '',
        SigningCertificateName: '',
        SigningGatewayName: '',
        EDIGatewayCertificateView: [],
    }

    // Gateway state (initial state is empty)
    const [gateway, setGateway] = React.useState<Gateway>(emptyGateway)
    const [isNewGateway, setIsNewGateway] = useState<boolean>(false)

    // State for deleting Gateway
    const [showDeleteGatewayModal, setShowDeleteGatewayModal] = useState<boolean>(false)
    const [gatewayToDelete, setGatewayToDelete] = useState<Gateway>(emptyGateway)

    // State for testing Certificate
    const [showTestCertificateModal, setShowTestCertificateModal] = useState<boolean>(false)
    const [testCertificateResponse, setTestCertificateResponse] = useState<string>('')
    const [testCertificateName, setTestCertificateName] = useState<string>('')

    const closeTestCertificateModal = () => {
        setShowTestCertificateModal(false)
    }

    // Handle onChanges for gateway modal
    const nameHandleChange = (event: any) => {
        setGateway({ ...gateway, Name: event.value })
    }
    const protocolHandleChange = (event: any) => {
        setGateway({ ...gateway, Protocol: event.value })
    }
    const deleteOnGetHandleChange = (event: any) => {
        setGateway({ ...gateway, DeleteOnGetRequired: event.value })
    }
    const isActiveHandleChange = (event: any) => {
        setGateway({ ...gateway, isActive: event.value })
    }
    const isANSIX12HandleChange = (event: any) => {
        setGateway({ ...gateway, isANSIX12: event.value })
    }
    const usernameHandleChange = (event: any) => {
        setGateway({ ...gateway, Username: event.value })
    }
    const passwordHandleChange = (event: any) => {
        setGateway({ ...gateway, Password: event.value })
    }
    const senderIDHandleChange = (event: any) => {
        setGateway({ ...gateway, AS2SenderID: event.value })
    }
    const URLHandleChange = (event: any) => {
        setGateway({ ...gateway, URL: event.value })
    }
    const portHandleChange = (event: any) => {
        setGateway({ ...gateway, Port: event.value })
    }
    const receiveDirectoryHandleChange = (event: any) => {
        setGateway({ ...gateway, ProviderReceiveDirectory: event.value })
    }
    const outboundDirectoryHandleChange = (event: any) => {
        setGateway({ ...gateway, OutBoundDirectory: event.value })
    }
    const sendDirectoryHandleChange = (event: any) => {
        setGateway({ ...gateway, ProviderSendDirectory: event.value })
    }
    const signingGatewayNameHandleChange = (event: any) => {
        setGateway({ ...gateway, SigningGatewayName: event.value })
    }
    const contactInfoHandleChange = (event: any) => {
        setGateway({ ...gateway, ContactInfo: event.value })
    }

    // Fetch data for grid
    async function fetchGridData() {
        await fetch(getEDIGatewayURL, {
            method: 'POST',
            mode: 'cors',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ includeCertificates: true }),
        })
            .then((response) => {
                if (response.ok) {
                    return response.json()
                }
                throw response
            })
            .then((data: EDIGatewayView) => {
                setData(data.EDIGatewayView)
            })
            .catch(async (error) => {
                const e = await error.text()
                console.error('Error fetching data: ', e)

                setError(e)
            })
            .finally(() => {
                setLoading(false)
            })
    }

    // Save gateway
    async function fetchSaveGateway(saveGateway: Gateway) {
        await fetch(saveEDIGatewayURL, {
            method: 'POST',
            mode: 'cors',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ EDIGatewayView: [saveGateway] }),
        })
            .then((response) => {
                if (response.ok) {
                    fetchGridData() // Refresh grid data
                    return response.json()
                }
                throw response
            })
            .catch(async (error) => {
                const e = await error.text()
                console.error('Error fetching data: ', e)

                setError(e)
            })
    }

    // Delete gateway
    async function fetchDeleteGateway(deleteGateway: Gateway) {
        await fetch(deleteEDIGatewayURL + '/' + deleteGateway.Name, {
            method: 'DELETE',
            mode: 'cors',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({}),
        })
            .then((response) => {
                if (response.ok) {
                    fetchGridData() // Refresh grid data
                    return response.json()
                }
                throw response
            })
            .catch(async (error) => {
                const e = await error.text()
                console.error('Error fetching data: ', e)

                setError(e)
            })
    }

    // Test certificate
    async function fetchTestCertificate(testGateway: Gateway) {
        if (testGateway.EDIGatewayCertificateView[0].PartnerCertificateName) {
            setTestCertificateName(testGateway.EDIGatewayCertificateView[0].PartnerCertificateName)
        } else {
            setTestCertificateName(testGateway.EDIGatewayCertificateView[0].SigningCertificateName)
        }

        await fetch(testCertificateURL, {
            method: 'POST',
            mode: 'cors',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body: JSON.stringify({ EDIGatewayView: [testGateway] }),
        })
            .then(async (response) => {
                if (response.ok) {
                    // Pop up modal with response
                    const resp = await response.json()
                    setTestCertificateResponse(resp)
                    setShowTestCertificateModal(true)
                    return resp
                }
                throw response
            })
            .catch(async (error) => {
                const e = await error.json()
                console.error('Error with testing certificate: ', e.ExceptionMessage)

                // Pop up modal
                setTestCertificateResponse(e.ExceptionMessage)
                setShowTestCertificateModal(true)
            })
    }

    // Handle submission of create/edit gateway
    const handleGatewaySubmit = (event: any) => {
        // Add gateway to database
        fetchSaveGateway({
            Name: event.values.Name,
            Username: event.values.Username,
            Password: event.values.Password,
            URL: event.values.URL,
            Port: event.values.Port,
            OutBoundDirectory: event.values.OutBoundDirectory,
            ProviderReceiveDirectory: event.values.ProviderReceiveDirectory,
            ProviderSendDirectory: event.values.ProviderSendDirectory,
            DeleteOnGetRequired: event.values.DeleteOnGetRequired,
            isActive: event.values.isActive,
            isANSIX12: event.values.isANSIX12,
            Protocol: event.values.Protocol,
            AS2SenderID: event.values.AS2SenderID,
            ContactInfo: event.values.ContactInfo,
            // PartnerCertificateExpires: event.values.PartnerCertificateExpires,
            CertificateName: event.values.CertificateName,
            SigningCertificateName: event.values.SigningCertificateName,
            SigningGatewayName: event.values.SigningGatewayName,
            EDIGatewayCertificateView: gateway.EDIGatewayCertificateView.map((gateway) => {
                return {
                    ExpirationDate: gateway.ExpirationDate,
                    GatewayName: event.values.Name,
                    PartnerCertificateName: gateway.PartnerCertificateName,
                    SigningCertificateName: gateway.SigningCertificateName,
                }
            }),
        })

        // Close gateway modal
        setOpenForm(false)
        setIsNewGateway(false)
    }

    // Delete gateway on row we click on
    const deleteGateway = (gateway: Gateway) => {
        setShowDeleteGatewayModal(true)
        setGatewayToDelete(gateway)
    }

    // Get the row we want to edit
    const editRowClick = (event: any) => {
        setIsNewGateway(false)
        enterEdit(event.dataItem)
    }

    // Edit gateway form opens and
    const enterEdit = (editGateway: Gateway) => {
        setGateway(editGateway)
        setOpenForm(true)
    }

    // Cancel create/edit gateway (close dialog without saving)
    const handleCancelDialog = () => {
        setIsNewGateway(false)
        setGateway(emptyGateway)
        fetchGridData() // Refresh grid data
        setOpenForm(false)
    }

    const setCertificate = (certificate: Certificate) => {
        setData((data) => {
            let newData = data?.map((gateway: Gateway) => {
                if (gateway.Name === certificate.GatewayName) {
                    let newView = gateway.EDIGatewayCertificateView.map((cert: Certificate) => {
                        if (
                            (cert.ExpirationDate === certificate.ExpirationDate &&
                                cert.PartnerCertificateName ===
                                    certificate.PartnerCertificateName) ||
                            (cert.ExpirationDate === certificate.ExpirationDate &&
                                cert.SigningCertificateName ===
                                    certificate.SigningCertificateName) ||
                            (cert.PartnerCertificateName === certificate.PartnerCertificateName &&
                                cert.SigningCertificateName === certificate.SigningCertificateName)
                        ) {
                            cert = {
                                ...certificate,
                            }
                        }

                        return cert
                    })
                    gateway.EDIGatewayCertificateView = newView
                    gateway.PartnerCertificateExpires = certificate.ExpirationDate
                }

                return gateway
            })

            return newData
        })
    }

    const deleteCertificate = (certificate: Certificate) => {
        setData((data) => {
            let newData = data?.map((gateway: Gateway) => {
                if (gateway.Name === certificate.GatewayName) {
                    let newView = gateway.EDIGatewayCertificateView.filter(
                        (cert) =>
                            cert.ExpirationDate !== certificate.ExpirationDate ||
                            cert.PartnerCertificateName !== certificate.PartnerCertificateName ||
                            cert.SigningCertificateName !== certificate.SigningCertificateName
                    )
                    gateway.EDIGatewayCertificateView = newView
                }

                return gateway
            })
            return newData
        })
    }

    const testCertificate = (certificate: Certificate) => {
        fetchTestCertificate({
            ...gateway,
            EDIGatewayCertificateView: [certificate],
        })
    }

    // Get data for Gateway on component load
    useEffect(() => {
        // POST request using fetch inside useEffect React hook
        fetchGridData()
    }, [])

    return (
        <div className="ediForms">
            <div className={styles.App}>
                <div className={styles.Header}>
                    <AWCButton
                        type={BUTTON_TYPES.PRIMARY}
                        onClick={() => {
                            setGateway(emptyGateway)
                            setIsNewGateway(true)
                            setOpenForm(true)
                        }}
                        style={{
                            position: 'absolute',
                            textAlign: 'center',
                            marginTop: '35px',
                            marginLeft: '20px',
                            left: 0,
                            cursor: 'pointer',
                        }}
                    >
                        Create new
                    </AWCButton>
                    <h1>EDI Gateways</h1>
                </div>

                <GridData
                    data={data}
                    loading={loading}
                    error={error}
                    editRowClick={editRowClick}
                    deleteGateway={deleteGateway}
                />

                {openForm && (
                    <GatewayModal
                        gateway={gateway}
                        isNewGateway={isNewGateway}
                        cancelDialog={handleCancelDialog}
                        onSubmit={handleGatewaySubmit}
                        setCertificate={setCertificate}
                        deleteCertificate={deleteCertificate}
                        testCertificate={testCertificate}
                        showTestCertificateModal={showTestCertificateModal}
                        testCertificateResponse={testCertificateResponse}
                        testCertificateName={testCertificateName}
                        closeTestCertificateModal={closeTestCertificateModal}
                        nameHandleChange={nameHandleChange}
                        protocolHandleChange={protocolHandleChange}
                        deleteOnGetHandleChange={deleteOnGetHandleChange}
                        isActiveHandleChange={isActiveHandleChange}
                        isANSIX12HandleChange={isANSIX12HandleChange}
                        usernameHandleChange={usernameHandleChange}
                        passwordHandleChange={passwordHandleChange}
                        senderIDHandleChange={senderIDHandleChange}
                        URLHandleChange={URLHandleChange}
                        portHandleChange={portHandleChange}
                        receiveDirectoryHandleChange={receiveDirectoryHandleChange}
                        outboundDirectoryHandleChange={outboundDirectoryHandleChange}
                        sendDirectoryHandleChange={sendDirectoryHandleChange}
                        signingGatewayNameHandleChange={signingGatewayNameHandleChange}
                        contactInfoHandleChange={contactInfoHandleChange}
                    />
                )}
                {showDeleteGatewayModal && (
                    <Dialog title="Delete Gateway" height="27.5%" width="40%" closeIcon={false}>
                        <p
                            style={{
                                margin: '5px',
                                textAlign: 'center',
                            }}
                        >
                            Are you sure you want to delete{' '}
                            <strong style={{ fontSize: 20 }}>{gatewayToDelete?.Name}</strong>{' '}
                            gateway?
                        </p>

                        <div className={styles.wrapFooter}>
                            <div className={`k-form-buttons ${styles.buttonLeft}`}>
                                <AWCButton
                                    type={BUTTON_TYPES.PRIMARY}
                                    onClick={() => {
                                        fetchDeleteGateway(gatewayToDelete)
                                        setShowDeleteGatewayModal(false)
                                    }}
                                    style={{
                                        width: 120,
                                    }}
                                >
                                    Delete
                                </AWCButton>
                            </div>

                            <div className={`k-form-buttons ${styles.buttonRight}`}>
                                <AWCButton
                                    type={BUTTON_TYPES.SECONDARY}
                                    onClick={() => {
                                        setShowDeleteGatewayModal(false)
                                    }}
                                >
                                    Cancel
                                </AWCButton>
                            </div>
                        </div>
                    </Dialog>
                )}
            </div>
        </div>
    )
}
